import { Box, createStyles, Group, Stack } from '@mantine/core';
import type { LineageFlowMonitor, MetricType } from '@repo/api-codegen';
import { MONITOR_ICON_MAPPING } from '@repo/common/constants';
import {
	formatMonitorMetricValue,
	isValidMonitorStatus,
	titleCase,
} from '@repo/common/utils';
import { Badge, Text } from '@repo/foundations';
import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router';

interface MonitorItemProps {
	monitor: LineageFlowMonitor;
}

const useStyles = createStyles((theme) => ({
	container: {
		cursor: 'pointer',
		padding: `${theme.spacing.sm} ${theme.spacing.md}`,
		'&:hover': {
			backgroundColor: theme.other.getColor('surface/primary/hover'),
		},
		'&:active, &:focus': {
			backgroundColor: theme.other.getColor('surface/primary/active'),
		},
	},
}));

function MonitorItem({ monitor }: MonitorItemProps) {
	const { classes } = useStyles();

	const navigate = useNavigate();

	const handleMonitorClick = useCallback(() => {
		navigate(`/monitor/${monitor.id}`);
	}, [navigate, monitor.id]);

	const subtitle = useMemo(
		() =>
			monitor.metric_value
				? `${titleCase(monitor.metric_type || '')} · ${formatMonitorMetricValue(
						monitor.metric_type as MetricType,
						parseFloat(monitor.metric_value)
					)}`
				: `${titleCase(monitor.metric_type || '')}`,
		[monitor.metric_type, monitor.metric_value]
	);

	return (
		<Box className={classes.container} onClick={handleMonitorClick}>
			<Group w="100%" position="apart">
				<Stack spacing={0}>
					<Text size="sm" weight="semibold">
						{monitor.title}
					</Text>
					<Text size="xs" color="text/secondary/default">
						{subtitle}
					</Text>
				</Stack>
				{isValidMonitorStatus(monitor.status) && (
					<Badge variant={MONITOR_ICON_MAPPING[monitor.status].variant}>
						{MONITOR_ICON_MAPPING[monitor.status].name}
					</Badge>
				)}
			</Group>
		</Box>
	);
}

export default MonitorItem;
